import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, TextField, Grid, Chip, Button,
  Autocomplete, CircularProgress, Alert, Divider, Avatar, List,
  ListItemButton, ListItemText, IconButton, Table, TableBody, TableRow, TableCell,
  Dialog, DialogContent, Snackbar, ImageList, ImageListItem,
  ImageListItemBar,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PetsIcon from '@mui/icons-material/Pets';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { collection, getDocs, query, where, getDoc, doc, updateDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';

import { COLORS } from '../theme/constants';

const { primary: IMG_PRIMARY, teal: IMG_TEAL } = COLORS;

const STATUS_CHIP = {
  reported:  { color: '#059669', bg: '#D1FAE5', label: 'Informado' },
  approved:  { color: '#16A34A', bg: '#DCFCE7', label: 'Aprobado' },
  draft:     { color: '#6B7280', bg: '#F3F4F6', label: 'Borrador' },
  critical:  { color: '#DC2626', bg: '#FEE2E2', label: 'Crítico' },
  unknown:   { color: '#6B7280', bg: '#F3F4F6', label: 'Desconocido' },
};

const getResultStatus = (r) => {
  if (r.reportedAt) return 'reported';
  if (r.approvedAt) return 'approved';
  return 'draft';
};

export default function PatientImagingHistory() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlPatientId = searchParams.get('patientId');
  const { currentUser, userData } = useAuth();
  const { filterByBranch } = useBranch();

  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [results, setResults] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loadingPatients, setLoadingPatients] = useState(true);
  const [loadingResults, setLoadingResults] = useState(false);
  const [loadingPatient, setLoadingPatient] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [resolvedUrls, setResolvedUrls] = useState({});

  useEffect(() => {
    getDocs(query(collection(db, 'patients')))
      .then((snap) => {
        const patientList = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPatients(filterByBranch(patientList, { includeUnassigned: true }));
        setLoadingPatients(false);

        if (urlPatientId) {
          const found = patientList.find((p) => p.id === urlPatientId);
          if (found) {
            setSelectedPatient(found);
          } else {
            setLoadingPatient(true);
            getDoc(doc(db, 'patients', urlPatientId))
              .then((s) => {
                if (s.exists()) {
                  const patient = { id: s.id, ...s.data() };
                  setPatients((prev) => [...prev, patient]);
                  setSelectedPatient(patient);
                }
              })
              .finally(() => setLoadingPatient(false));
          }
        }
      });
  }, [urlPatientId]);

  useEffect(() => {
    if (!selectedPatient) { setResults([]); setSelectedResult(null); return; }
    setLoadingResults(true);
    getDocs(query(
      collection(db, 'imagingResults'),
      where('patientId', '==', selectedPatient.id)
    )).then((snap) => {
      const list = filterByBranch(snap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true });
      list.sort((a, b) => {
        const dateA = a.reportedAt || a.approvedAt || a.createdAt;
        const dateB = b.reportedAt || b.approvedAt || b.createdAt;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateB.toDate ? dateB.toDate() - dateA.toDate() : dateB - dateA;
      });
      setResults(list);
      setLoadingResults(false);
    });
  }, [selectedPatient]);

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  useEffect(() => {
    if (!selectedResult?.orderId) { setSelectedOrder(null); return; }
    setLoadingOrder(true);
    getDoc(doc(db, 'imagingOrders', selectedResult.orderId))
      .then((s) => {
        if (s.exists()) setSelectedOrder({ id: s.id, ...s.data() });
        else setSelectedOrder(null);
      })
      .catch(() => setSelectedOrder(null))
      .finally(() => setLoadingOrder(false));
  }, [selectedResult]);

  // Resolve image URLs when stored downloadURL is missing or stale
  useEffect(() => {
    if (!selectedOrder?.files) return;
    selectedOrder.files.forEach((file) => {
      const key = file.storagePath || file.id || file.fileName;
      if (resolvedUrls[key] || file.downloadURL || !file.storagePath) return;
      getDownloadURL(ref(storage, file.storagePath))
        .then((url) => setResolvedUrls((prev) => ({ ...prev, [key]: url })))
        .catch((err) => console.error('Error resolviendo URL de imagen:', err));
    });
  }, [selectedOrder, resolvedUrls]);

  const handleFileUpload = async (files) => {
    if (!selectedOrder || files.length === 0) return;
    setUploading(true);
    const uploadedFiles = [];
    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) continue;
        const storageRef = ref(storage, `imagingStudies/${selectedOrder.id}/${Date.now()}_${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);
        await new Promise((resolve, reject) => {
          uploadTask.on('state_changed',
            (snapshot) => setUploadProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)),
            reject,
            async () => {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              uploadedFiles.push({
                id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                fileName: file.name,
                fileType: file.type,
                storagePath: storageRef.fullPath,
                downloadURL: url,
                fileSizeMB: (file.size / 1048576).toFixed(2),
                uploadedBy: currentUser?.uid || '',
                uploadedAt: new Date(),
              });
              resolve();
            }
          );
        });
      }
      if (uploadedFiles.length > 0) {
        await updateDoc(doc(db, 'imagingOrders', selectedOrder.id), {
          files: arrayUnion(...uploadedFiles),
          updatedAt: serverTimestamp(),
        });
        setSelectedOrder((prev) => prev ? { ...prev, files: [...(prev.files || []), ...uploadedFiles] } : prev);
        setSnackbar({ open: true, message: `${uploadedFiles.length} imagen(es) subida(s)`, severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Selecciona archivos JPG/PNG', severity: 'warning' });
      }
    } catch (err) {
      console.error('Upload error:', err);
      setSnackbar({ open: true, message: `Error al subir: ${err.message}`, severity: 'error' });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const images = selectedOrder?.files?.filter((f) => (f.fileType || '').startsWith('image/')) || [];

  const openViewer = (idx) => { setViewerIndex(idx); setViewerOpen(true); };
  const closeViewer = () => setViewerOpen(false);
  const prevImage = () => setViewerIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  const nextImage = () => setViewerIndex((i) => (i === images.length - 1 ? 0 : i + 1));

  const getImageKey = (file) => file.storagePath || file.id || file.fileName;
  const getImageUrl = (file) => resolvedUrls[getImageKey(file)] || file.downloadURL || '';
  const handleImageError = (file) => () => {
    const key = getImageKey(file);
    if (resolvedUrls[key] || !file.storagePath) return;
    getDownloadURL(ref(storage, file.storagePath))
      .then((url) => setResolvedUrls((prev) => ({ ...prev, [key]: url })))
      .catch(() => {});
  };

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        {urlPatientId && (
          <IconButton onClick={() => navigate(`/patients/${urlPatientId}/record`)} sx={{ color: IMG_PRIMARY }}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <BrokenImageIcon sx={{ color: IMG_PRIMARY, fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700} color={IMG_PRIMARY}>Historial de Imágenes</Typography>
          <Typography variant="caption" color="text.secondary">Resultados e informes por paciente</Typography>
        </Box>
      </Box>

      {/* Patient selector - solo mostrar si no viene de ficha clínica */}
      {!urlPatientId && (
        <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
          <Autocomplete
            options={patients}
            getOptionLabel={(p) => `${p.name} (${p.species || 'N/D'} — ${p.breed || ''})`}
            value={selectedPatient}
            onChange={(_, v) => { setSelectedPatient(v); setSelectedResult(null); }}
            loading={loadingPatients}
            renderInput={(params) => (
              <TextField {...params} label="Buscar paciente" size="small" sx={{ maxWidth: 420 }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <PetsIcon sx={{ fontSize: 18, color: 'text.disabled', mr: 0.5 }} />,
                }}
              />
            )}
          />
          {selectedPatient && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: IMG_TEAL, width: 44, height: 44 }}>
                <PetsIcon />
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight={700}>{selectedPatient.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedPatient.species} · {selectedPatient.breed} · {selectedPatient.sex}
                  {selectedPatient.weight ? ` · ${selectedPatient.weight} kg` : ''}
                </Typography>
              </Box>
              <Chip label={`${results.length} informe(s)`} size="small"
                sx={{ bgcolor: '#EFF6FF', color: IMG_PRIMARY, fontWeight: 700 }} />
            </Box>
          )}
        </Paper>
      )}

      {/* Mostrar info del paciente cuando viene de ficha clínica */}
      {urlPatientId && selectedPatient && (
        <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
          {loadingPatient ? (
            <CircularProgress size={24} />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: IMG_TEAL, width: 44, height: 44 }}>
                <PetsIcon />
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight={700}>{selectedPatient.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedPatient.species} · {selectedPatient.breed} · {selectedPatient.sex}
                  {selectedPatient.weight ? ` · ${selectedPatient.weight} kg` : ''}
                </Typography>
              </Box>
              <Chip label={`${results.length} informe(s)`} size="small"
                sx={{ bgcolor: '#EFF6FF', color: IMG_PRIMARY, fontWeight: 700 }} />
            </Box>
          )}
        </Paper>
      )}

      {selectedPatient && (
        <Grid container spacing={3}>
          {/* LEFT: results timeline */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
              <Box sx={{ p: 1.5, bgcolor: IMG_PRIMARY }}>
                <Typography variant="subtitle2" fontWeight={700} color="#fff">
                  <BrokenImageIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  Historial de estudios
                </Typography>
              </Box>
              {loadingResults ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={28} /></Box>
              ) : results.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <Typography color="text.secondary" variant="body2" textAlign="center">
                    Sin estudios registrados.
                  </Typography>
                </Box>
              ) : (
                <List dense sx={{ p: 0, maxHeight: '60vh', overflowY: 'auto' }}>
                  {results.map((r, i) => {
                    const status = getResultStatus(r);
                    const stCfg = STATUS_CHIP[status] || STATUS_CHIP.unknown;
                    const hasCritical = (r.findings || []).some((f) => f.criticalFinding);
                    const examNames = (r.findings || []).map((f) => f.examTypeName).filter(Boolean).join(', ') || 'Estudio';
                    return (
                      <React.Fragment key={r.id}>
                        {i > 0 && <Divider />}
                        <ListItemButton
                          selected={selectedResult?.id === r.id}
                          onClick={() => setSelectedResult(r)}
                          sx={{ '&.Mui-selected': { bgcolor: '#EFF6FF' } }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                <Typography variant="body2" fontWeight={700} noWrap sx={{ maxWidth: 120 }}>
                                  {r.orderNumber || 'Informe'}
                                </Typography>
                                {hasCritical && (
                                  <Chip label="Crítico" size="small"
                                    sx={{ bgcolor: '#FEE2E2', color: '#DC2626', fontSize: '0.6rem', height: 16, fontWeight: 700 }} />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="text.secondary">{formatDate(r.reportedAt || r.approvedAt || r.createdAt)}</Typography>
                                <Chip label={stCfg.label} size="small"
                                  sx={{ ml: 0.5, bgcolor: stCfg.bg, color: stCfg.color, fontSize: '0.6rem', height: 16 }} />
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </Paper>
          </Grid>

          {/* RIGHT: result detail */}
          <Grid size={{ xs: 12, md: 8 }}>
            {selectedResult ? (
              <Box>
                <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid #E2E8F0' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} color={IMG_PRIMARY}>
                      Orden {selectedResult.orderNumber || '–'} — {formatDate(selectedResult.reportedAt || selectedResult.approvedAt || selectedResult.createdAt)}
                    </Typography>
                    <Button size="small" variant="outlined"
                      onClick={() => navigate(`/imaging/orders/${selectedResult.orderId}`)}
                      sx={{ borderColor: IMG_TEAL, color: IMG_TEAL, fontSize: '0.75rem' }}>
                      Ver orden
                    </Button>
                  </Box>
                  {selectedResult.radiologistName && (
                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                      Radiólogo: {selectedResult.radiologistName} {selectedResult.radiologistSignature ? `· ${selectedResult.radiologistSignature}` : ''}
                    </Typography>
                  )}
                  {selectedResult.reportFile && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: '#F0FDF4', borderRadius: 2, mb: 2 }}>
                      <PictureAsPdfIcon color="success" />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{selectedResult.reportFile.fileName}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                          {new Date(selectedResult.reportFile.uploadedAt).toLocaleString('es-CL')}
                        </Typography>
                      </Box>
                      <Button size="small" variant="outlined" href={selectedResult.reportFile.downloadURL} target="_blank" rel="noopener" startIcon={<VisibilityIcon />}>
                        Ver informe
                      </Button>
                    </Box>
                  )}
                  {selectedResult.overallImpression && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight={700}>Impresión general</Typography>
                      <Typography variant="body2">{selectedResult.overallImpression}</Typography>
                    </Alert>
                  )}
                  {(selectedResult.findings || []).map((f, idx) => (
                    <Paper key={idx} elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FAFBFC' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={700} color={IMG_PRIMARY}>
                          {f.examTypeName || 'Estudio'} {f.modality ? `· ${f.modality}` : ''}
                        </Typography>
                        {f.criticalFinding && (
                          <Chip label="Hallazgo crítico" size="small"
                            sx={{ bgcolor: '#FEE2E2', color: '#DC2626', fontSize: '0.65rem', height: 18, fontWeight: 700 }} />
                        )}
                      </Box>
                      {f.technique && (
                        <Typography variant="body2" color="text.secondary" mb={1}>
                          <strong>Técnica:</strong> {f.technique}
                        </Typography>
                      )}
                      {f.findings && (
                        <Box mb={1.5}>
                          <Typography variant="caption" fontWeight={700} color={IMG_PRIMARY}>HALLAZGOS</Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{f.findings}</Typography>
                        </Box>
                      )}
                      {f.impression && (
                        <Box mb={1.5}>
                          <Typography variant="caption" fontWeight={700} color={IMG_PRIMARY}>IMPRESIÓN DIAGNÓSTICA</Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{f.impression}</Typography>
                        </Box>
                      )}
                      {f.recommendation && (
                        <Box mb={1.5}>
                          <Typography variant="caption" fontWeight={700} color={IMG_PRIMARY}>RECOMENDACIÓN</Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{f.recommendation}</Typography>
                        </Box>
                      )}
                      {f.measurements?.length > 0 && (
                        <Box>
                          <Typography variant="caption" fontWeight={700} color={IMG_PRIMARY}>MEDICIONES</Typography>
                          <Table size="small" sx={{ mt: 0.5 }}>
                            <TableBody>
                              {f.measurements.map((m, mi) => (
                                <TableRow key={mi}>
                                  <TableCell sx={{ fontWeight: 600, pl: 0 }}>{m.structure || '–'}</TableCell>
                                  <TableCell>{m.value || '–'}</TableCell>
                                  <TableCell>
                                    <Chip label={m.normal ? 'Normal' : 'Anormal'} size="small"
                                      sx={{ fontSize: '0.65rem', height: 18, bgcolor: m.normal ? '#DCFCE7' : '#FEE2E2', color: m.normal ? '#16A34A' : '#DC2626' }} />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      )}
                    </Paper>
                  ))}
                </Paper>

                {/* Imágenes del estudio asociado */}
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #E2E8F0' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} color={IMG_PRIMARY}>
                      Imágenes del estudio
                    </Typography>
                    <Button
                      variant="outlined" size="small" component="label" startIcon={<UploadFileIcon />}
                      disabled={uploading || !selectedOrder} sx={{ borderColor: IMG_TEAL, color: IMG_TEAL, fontSize: '0.75rem' }}>
                      {uploading ? `Subiendo ${uploadProgress}%` : 'Subir JPG'}
                      <input
                        type="file" accept="image/jpeg,image/jpg,image/png" multiple hidden
                        onChange={(e) => { handleFileUpload(Array.from(e.target.files)); e.target.value = ''; }}
                      />
                    </Button>
                  </Box>
                  {loadingOrder ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={24} /></Box>
                  ) : !selectedOrder ? (
                    <Alert severity="info" sx={{ fontSize: '0.85rem' }}>No se encontró la orden asociada a este informe.</Alert>
                  ) : images.length === 0 ? (
                    <Alert severity="info" sx={{ fontSize: '0.85rem' }}>Aún no hay imágenes subidas para este estudio.</Alert>
                  ) : (
                    <ImageList sx={{ m: 0 }} cols={3} gap={8}>
                      {images.map((file, idx) => (
                        <ImageListItem key={file.id || idx} sx={{ cursor: 'pointer' }} onClick={() => openViewer(idx)}>
                          <img
                            src={getImageUrl(file)} alt={file.fileName}
                            loading="lazy"
                            onError={handleImageError(file)}
                            style={{ borderRadius: 8, objectFit: 'cover', height: 120, width: '100%', background: '#F1F5F9' }}
                          />
                          <ImageListItemBar
                            title={file.fileName}
                            position="below"
                            sx={{ '& .MuiImageListItemBar-titleWrap': { p: '4px 0 0 0' } }}
                            titleTypographyProps={{ fontSize: '0.65rem', color: 'text.secondary', noWrap: true }}
                          />
                        </ImageListItem>
                      ))}
                    </ImageList>
                  )}
                </Paper>

                {/* Image viewer dialog */}
                <Dialog open={viewerOpen} onClose={closeViewer} maxWidth="lg" fullWidth>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, bgcolor: '#0F172A' }}>
                    <Typography color="#fff" variant="body2" fontWeight={600}>
                      {images[viewerIndex]?.fileName || ''} ({viewerIndex + 1}/{images.length})
                    </Typography>
                    <IconButton onClick={closeViewer} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
                  </Box>
                  <DialogContent sx={{ bgcolor: '#0F172A', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                    {images[viewerIndex] && (
                      <img
                        src={getImageUrl(images[viewerIndex])}
                        alt={images[viewerIndex].fileName}
                        onError={handleImageError(images[viewerIndex])}
                        style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8 }}
                      />
                    )}
                  </DialogContent>
                  {images.length > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, bgcolor: '#0F172A' }}>
                      <Button startIcon={<NavigateBeforeIcon />} onClick={prevImage} sx={{ color: '#fff' }}>Anterior</Button>
                      <Button endIcon={<NavigateNextIcon />} onClick={nextImage} sx={{ color: '#fff' }}>Siguiente</Button>
                    </Box>
                  )}
                </Dialog>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                height: 360, color: '#CBD5E1' }}>
                <BrokenImageIcon sx={{ fontSize: 64 }} />
                <Typography color="text.disabled" mt={1}>
                  Selecciona un informe para ver el detalle
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      )}
      {!selectedPatient && !urlPatientId && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 300, color: '#CBD5E1' }}>
          <PetsIcon sx={{ fontSize: 72 }} />
          <Typography color="text.disabled" mt={1}>Busca un paciente para ver su historial de imágenes</Typography>
        </Box>
      )}

      <Snackbar
        open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
